import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

export type MapType = 'streets' | 'satellite' | 'dark' | 'light' | 'outdoors' | 'satellite-streets';

interface MapStyleOption {
  value: MapType;
  label: string;
  thumbnail: string;
  description?: string;
}

interface MapStylePopupProps {
  open: boolean;
  onClose: () => void;
  selectedMapType: MapType;
  onMapTypeChange: (mapType: MapType) => void;
}

const mapStyleOptions: MapStyleOption[] = [
  {
    value: 'streets',
    label: 'Streets',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Street map'
  },
  {
    value: 'satellite',
    label: 'Satellite',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Satellite imagery'
  },
  {
    value: 'satellite-streets',
    label: 'Satellite Streets',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Satellite with streets'
  },
  {
    value: 'dark',
    label: 'Dark',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Dark theme'
  },
  {
    value: 'light',
    label: 'Light',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Light theme'
  },
  {
    value: 'outdoors',
    label: 'Outdoors',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Outdoor recreation'
  }
];

const MapStylePopup: React.FC<MapStylePopupProps> = ({
  open,
  onClose,
  selectedMapType,
  onMapTypeChange,
}) => {
  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        top: 60,
        left: 20,
        width: 400,
        maxHeight: 500,
        zIndex: 1000,
        overflow: 'auto',
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Choose a map style
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Map Style Grid */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {mapStyleOptions.map((option) => (
            <Grid item xs={4} key={option.value}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedMapType === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': {
                    border: '2px solid #1976d2',
                    boxShadow: 2,
                  },
                }}
                onClick={() => {
                  onMapTypeChange(option.value);
                  onClose();
                }}
              >
                <CardMedia
                  component="img"
                  height="80"
                  image={option.thumbnail}
                  alt={option.label}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    {option.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

export default MapStylePopup;
